import { z } from "zod";
import { DIRECT_916_PLATFORM_SAFE_ZONE_DIRECTIVE, PLATFORM_SAFE_RECT_916 } from "./aspectRatios";

export const direct916CandidateSchema = z.object({
  imageUrl: z.string(),
  falRequestId: z.string().nullable(),
  model: z.enum(["gpt-image-2", "gpt-image-2.5", "flux-2-pro", "nano-banana-2", "seedream-4"]),
  supplierEndpoint: z.string(),
  customerPricingModel: z.string(),
  tier: z.string(),
  bandSource: z.enum(["outpainted", "qa_downgraded", "failure_fallback", "person_seam_fallback"]).optional(),
  bandAttempts: z.number().optional(),
  portraitOutcome: z.any().optional(),
});
export type Direct916Candidate = z.infer<typeof direct916CandidateSchema>;
const attemptSchema = z.object({
  status: z.enum(["submitting", "submitted", "done", "failed"]),
  requestId: z.string().optional(),
  candidate: direct916CandidateSchema.optional(),
  render: z.object({
    endpoint: z.string(), payload: z.record(z.unknown()),
    model: direct916CandidateSchema.shape.model,
    customerPricingModel: z.string(), tier: z.string(),
  }).optional(),
  extensionStarted: z.boolean().optional(),
});
export const direct916GuardStateSchema = z.object({
  version: z.literal(1),
  // The original pre-reference prompt is never replaced by corrective guidance.
  basePrompt: z.string(),
  original: direct916CandidateSchema,
  endpoint: z.string(),
  payload: z.record(z.unknown()),
  fallbackUsesOriginalEngine: z.boolean().optional(),
  correction: attemptSchema.optional(),
  fallback: attemptSchema.optional(),
  finished: z.boolean().optional(),
  extension: z.object({
    useEnhancedRender: z.boolean(), seamBlend: z.boolean(), personSeamGuard: z.boolean(),
    portraitContinuation: z.boolean(),
  }).optional(),
});
export type Direct916GuardState = z.infer<typeof direct916GuardStateSchema>;
export type Direct916Violation = {
  kind: "text" | "logo" | "cta";
  text?: string;
  box: { x: number; y: number; w: number; h: number };
};

export function direct916GuardApplies(args: {
  standard: boolean; isMeme: boolean; ratio: string; mode?: string; enabled: boolean;
}): boolean {
  return args.standard && !args.isMeme && args.ratio === "9:16" &&
    args.mode === "direct_916" && args.enabled;
}

export function direct916Prompt(prompt: string, violations: Direct916Violation[] = []): string {
  const suffix = violations.slice(0, 5).map(({ text, box }) =>
    `The previous attempt placed this text in a platform UI zone: "${(text ?? "").replace(/[\r\n"]/g, " ")}" at y=${box.y} to ${box.y + box.h}. Move it inside y=${PLATFORM_SAFE_RECT_916.top} to y=${PLATFORM_SAFE_RECT_916.bottom}.`,
  ).join(" ");
  return `${prompt}\n\n${DIRECT_916_PLATFORM_SAFE_ZONE_DIRECTIVE}${suffix ? `\n\n${suffix}` : ""}`;
}
